import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Facebook, Linkedin, Menu, Moon, Sun, Twitter, X } from 'lucide-react';
import { SOCIALS, assetUrl } from '../data';
import { EASE, useApp, useSite } from './shared';

const LINKS = [
  ['Overview', 'overview'],
  ['Themes', 'topics'],
  ['Summit', 'highlights'],
  ['Industries', 'industries'],
  ['Speakers', 'speakers'],
  ['Agenda', 'agenda'],
  ['Partners', 'partners'],
  ['Contact Us', 'contact'],
];

const SOCIAL_ICONS = { facebook: Facebook, twitter: Twitter, linkedin: Linkedin };

export default function Nav() {
  const { scrollToId, openModal } = useApp();
  const { settings } = useSite();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [light, setLight] = useState(
    () => typeof window !== 'undefined' && localStorage.getItem('cxo-theme') === 'light'
  );

  useEffect(() => {
    document.documentElement.classList.toggle('light', light);
    localStorage.setItem('cxo-theme', light ? 'light' : 'dark');
  }, [light]);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    fn();
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled ? 'glass border-b border-line/10 py-3' : 'bg-transparent py-5'
        }`}
      >
        <div className="mx-auto flex w-full max-w-[1440px] items-center justify-between px-5 md:px-8">
          <button
            data-testid="nav-logo"
            onClick={() => go('home')}
            className="group flex items-center text-left"
            aria-label="CIO AXIS — back to top"
          >
            <img
              src={assetUrl(settings.logo_header)}
              alt="CIO AXIS"
              className="logo-on-dark h-7 w-auto object-contain transition-transform duration-500 group-hover:scale-[1.04] md:h-8"
            />
          </button>

          <nav className="hidden items-center gap-5 lg:flex">
            {LINKS.map(([label, id]) => (
              <button
                key={id}
                data-testid={`nav-link-${id}`}
                onClick={() => go(id)}
                className="whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.16em] text-slate-300 transition-colors hover:text-lime"
              >
                {label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="theme-toggle"
              onClick={() => setLight(!light)}
              aria-label="toggle color theme"
              title={light ? 'Switch to dark theme' : 'Switch to light theme'}
              className="flex h-9 w-9 items-center justify-center rounded-md border border-line/10 text-slate-300 transition-all hover:border-lime/60 hover:text-lime"
            >
              {light ? <Moon size={14} /> : <Sun size={14} />}
            </button>
            <div className="hidden items-center gap-2 xl:flex">
              {SOCIALS.map((s, i) => {
                const Icon = SOCIAL_ICONS[s.name];
                return (
                  <a
                    key={s.name}
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`nav-social-${i}`}
                    className="flex h-8 w-8 items-center justify-center rounded-md border border-line/10 text-slate-400 transition-all hover:border-lime/60 hover:text-lime"
                    aria-label={s.name}
                  >
                    <Icon size={13} />
                  </a>
                );
              })}
            </div>
            <button
              data-testid="nav-register-button"
              onClick={() => openModal('register')}
              className="hidden rounded-full bg-lime px-5 py-2.5 font-display text-xs font-bold uppercase tracking-widest text-ink transition-all hover:shadow-[0_0_28px_rgba(153,204,0,0.45)] sm:block"
            >
              Express Interest
            </button>
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen(!open)}
              className="flex h-10 w-10 items-center justify-center rounded-md border border-line/10 lg:hidden"
              aria-label="menu"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="glass fixed inset-0 z-40 flex flex-col justify-center px-8 lg:hidden"
          >
            <div className="flex flex-col gap-2">
              {LINKS.map(([label, id], i) => (
                <motion.button
                  key={id}
                  data-testid={`nav-mobile-link-${id}`}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.5, ease: EASE }}
                  onClick={() => go(id)}
                  className="border-b border-line/5 py-4 text-left font-display text-3xl font-bold tracking-tight text-slate-100"
                >
                  <span className="mr-4 font-mono text-xs text-lime">0{i + 1}</span>
                  {label}
                </motion.button>
              ))}
            </div>
            <motion.button
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5, ease: EASE }}
              data-testid="nav-mobile-register-button"
              onClick={() => {
                setOpen(false);
                openModal('register');
              }}
              className="mt-10 rounded-full bg-lime px-6 py-4 font-display text-sm font-bold uppercase tracking-widest text-ink"
            >
              Express Interest
            </motion.button>

          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
